import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as UsersService from '../services/UsersService';
import { User } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const usersKeys = {
    all: ['users'] as const,
    lists: () => [...usersKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...usersKeys.lists(), { filters }] as const,
    details: () => [...usersKeys.all, 'detail'] as const,
    detail: (username: string) => [...usersKeys.details(), username] as const,
};

// Hooks
export const useUsersQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: usersKeys.list(filters),
        queryFn: () => UsersService.fetchRecords(filters),
    });
};

export const useUserQuery = (username: string) => {
    return useQuery({
        queryKey: usersKeys.detail(username),
        queryFn: () => UsersService.fetch(username),
        enabled: !!username,
    });
};

export const useCreateUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: UsersService.add,
        onSuccess: () => {
            notifySuccess('User created');
            queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ username, record }: { username: string; record: User }) =>
            UsersService.update(username, record),
        onSuccess: (_, { username }) => {
            notifySuccess('User updated');
            queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
            queryClient.invalidateQueries({ queryKey: usersKeys.detail(username) });
        },
        onError: notifyError,
    });
};

export const useDeleteUserMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: UsersService.deleteRecord,
        onSuccess: () => {
            notifySuccess('User deleted');
            queryClient.invalidateQueries({ queryKey: usersKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useUsersFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
